import Play from "./Play";
import DiscImageDevice from "./DiscImageDevice";

export let PlayModule: any = null;
export let lastInitStage = "idle";

export type BrowserProbe = {
  userAgent: string;
  webAssembly: boolean;
  webgl2: boolean;
  secureContext: boolean;
  visibility: string;
  standalone: boolean;
  crossOriginIsolated: boolean;
  sharedArrayBuffer: boolean;
  sharedWasmMemory: boolean;
};

export function probeBrowser(): BrowserProbe {
  let webgl2 = false;
  let sharedWasmMemory = false;
  try {
    const canvas = document.createElement("canvas");
    webgl2 = Boolean(canvas.getContext("webgl2"));
  } catch {
    webgl2 = false;
  }

  try {
    if (typeof WebAssembly !== "undefined") {
      new WebAssembly.Memory({ initial: 1, maximum: 1, shared: true } as any);
      sharedWasmMemory = true;
    }
  } catch {
    sharedWasmMemory = false;
  }

  return {
    userAgent: navigator.userAgent,
    webAssembly: typeof WebAssembly !== "undefined",
    webgl2,
    secureContext: window.isSecureContext,
    visibility: document.visibilityState,
    standalone: Boolean((navigator as any).standalone),
    crossOriginIsolated: Boolean((globalThis as any).crossOriginIsolated),
    sharedArrayBuffer: typeof (globalThis as any).SharedArrayBuffer !== "undefined",
    sharedWasmMemory,
  };
}

function stage(value: string) {
  lastInitStage = value;
  window.dispatchEvent(new CustomEvent("genie-play-init-stage", { detail: value }));
}

function timeout<T>(promise: Promise<T>, milliseconds: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      window.setTimeout(
        () => reject(new Error(`${label} timed out after ${Math.round(milliseconds / 1000)} seconds`)),
        milliseconds,
      ),
    ),
  ]);
}

export async function initPlayModule() {
  if (PlayModule !== null) return PlayModule;

  const probe = probeBrowser();
  if (!probe.webAssembly) {
    throw new Error("This browser does not expose WebAssembly.");
  }
  if (!probe.webgl2) {
    throw new Error("WebGL 2 is unavailable in this browser/webview. Open the page in full Safari and try again.");
  }
  if (!probe.crossOriginIsolated || !probe.sharedArrayBuffer || !probe.sharedWasmMemory) {
    throw new Error(
      "Play! is a multithreaded WebAssembly build and this browser context is not cross-origin isolated with SharedArrayBuffer enabled. Open this page in full Safari after the isolation-header update and try again.",
    );
  }

  const baseURL =
    window.location.origin +
    window.location.pathname.substring(0, window.location.pathname.lastIndexOf("/"));

  const locateFile = (path: string) => `${baseURL}/${path}`;

  stage("fetching Play.wasm");
  let wasmBinary: Uint8Array;
  try {
    const response = await timeout(fetch(locateFile("Play.wasm"), { cache: "no-store" }), 15000, "Play.wasm download");
    if (!response.ok) {
      throw new Error(`Play.wasm returned HTTP ${response.status}`);
    }
    const bytes = await timeout(response.arrayBuffer(), 15000, "Play.wasm read");
    wasmBinary = new Uint8Array(bytes);
    if (wasmBinary.byteLength < 1024) {
      throw new Error(`Play.wasm download is unexpectedly small (${wasmBinary.byteLength} bytes)`);
    }
  } catch (error) {
    stage("failed while fetching Play.wasm");
    throw error;
  }

  stage(`compiling Play.wasm (${Math.round(wasmBinary.byteLength / 1024)} KiB)`);
  try {
    await timeout(WebAssembly.compile(wasmBinary), 30000, "Play.wasm compile");
  } catch (error) {
    stage("failed while compiling Play.wasm");
    throw error;
  }

  stage("starting multithreaded Play runtime");
  const moduleOverrides = {
    locateFile,
    mainScriptUrlOrBlob: locateFile("Play.js"),
    wasmBinary,
    print: (text: string) => console.log(`[Play] ${text}`),
    printErr: (text: string) => console.error(`[Play] ${text}`),
    onAbort: (reason: unknown) => console.error("[Play abort]", reason),
    monitorRunDependencies: (left: number) => stage(`Play runtime dependencies remaining: ${left}`),
  };

  let module: any;
  try {
    module = await timeout(Play(moduleOverrides), 45000, "Play multithreaded runtime initialization");
  } catch (error) {
    stage("failed while starting Play pthread runtime");
    throw error;
  }

  stage("preparing virtual filesystem");
  try {
    try {
      module.FS.mkdir("/work");
    } catch (error: any) {
      // EEXIST is harmless if initialization is retried after a partial start.
      if (error?.errno !== 20 && !String(error).includes("File exists")) throw error;
    }
    module.discImageDevice = new DiscImageDevice(module);
  } catch (error) {
    stage("failed while preparing filesystem");
    throw error;
  }

  stage("creating PS2 virtual machine");
  try {
    module.ccall("initVm", "", [], []);
  } catch (error) {
    stage("failed while creating PS2 virtual machine");
    throw error;
  }

  PlayModule = module;
  stage("ready");
  return PlayModule;
}
